import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";
import { errorMessage } from "../lib/errors";
import { Mail, Lock, User as UserIcon } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    full_name: "", email: "", password: "",
    parental_consent: false, accept_terms: false,
    referral_code: params.get("ref") || "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_terms) return toast.error("Vous devez accepter les conditions générales.");
    if (form.password.length < 8) return toast.error("Le mot de passe doit faire au moins 8 caractères.");
    setBusy(true);
    try {
      const u = await register({
        full_name: form.full_name,
        email: form.email,
        password: form.password,
        parental_consent: form.parental_consent,
        referral_code: form.referral_code || undefined,
      });
      toast.success(`Bienvenue ${u.full_name.split(" ")[0]} ! 🎉`);
      nav("/dashboard");
    } catch (err) {
      toast.error(errorMessage(err, "Inscription échouée"));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="register-page">
      <div className="text-xs uppercase tracking-widest text-gold-700 font-mono mb-3">Bienvenue</div>
      <h1 className="font-display text-4xl text-ink-900 mb-2">Créer mon compte</h1>
      <p className="text-muted2 text-sm mb-8">C'est gratuit, et ça prend 30 secondes.</p>

      <form onSubmit={submit} className="glass rounded-2xl p-6 space-y-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-gold-700 font-mono mb-1.5 block">Nom complet</label>
          <div className="relative">
            <UserIcon className="w-4 h-4 absolute left-3 top-3.5 text-muted2" />
            <input required value={form.full_name} onChange={(e)=>set("full_name", e.target.value)} placeholder="Adrien Mallet"
              className="w-full bg-white border border-gold-200 rounded-lg pl-10 pr-3 py-3 text-ink-900 placeholder:text-ink-400 focus:outline-none focus:border-gold-400"
              data-testid="register-fullname-input" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-gold-700 font-mono mb-1.5 block">Email</label>
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3 top-3.5 text-muted2" />
            <input type="email" required value={form.email} onChange={(e)=>set("email", e.target.value)} placeholder="vous@email.com"
              className="w-full bg-white border border-gold-200 rounded-lg pl-10 pr-3 py-3 text-ink-900 placeholder:text-ink-400 focus:outline-none focus:border-gold-400"
              data-testid="register-email-input" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-gold-700 font-mono mb-1.5 block">Mot de passe (min. 8)</label>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3 top-3.5 text-muted2" />
            <input type="password" required minLength={8} value={form.password} onChange={(e)=>set("password", e.target.value)} placeholder="••••••••"
              className="w-full bg-white border border-gold-200 rounded-lg pl-10 pr-3 py-3 text-ink-900 placeholder:text-ink-400 focus:outline-none focus:border-gold-400"
              data-testid="register-password-input" />
          </div>
        </div>
        {form.referral_code !== undefined && (
          <div>
            <label className="text-xs uppercase tracking-widest text-gold-700 font-mono mb-1.5 block">Code parrainage (facultatif)</label>
            <input value={form.referral_code} onChange={(e)=>set("referral_code", e.target.value.toUpperCase())} placeholder="EX: ABCD1234"
              className="w-full bg-white border border-gold-200 rounded-lg px-3 py-3 text-ink-900 placeholder:text-ink-400 focus:outline-none focus:border-gold-400 font-mono"
              data-testid="register-referral-input" />
          </div>
        )}

        <label className="flex items-start gap-2 text-sm text-muted2 cursor-pointer">
          <input type="checkbox" checked={form.parental_consent} onChange={(e)=>set("parental_consent", e.target.checked)} data-testid="register-parental-checkbox" className="mt-1" />
          J'ai l'accord de mes parents pour créer un compte (obligatoire si vous avez moins de 18 ans).
        </label>
        <label className="flex items-start gap-2 text-sm text-muted2 cursor-pointer">
          <input type="checkbox" required checked={form.accept_terms} onChange={(e)=>set("accept_terms", e.target.checked)} data-testid="register-terms-checkbox" className="mt-1" />
          J'accepte les <Link to="/terms" className="text-gold-700 hover:text-gold-700-700-600">conditions générales</Link> et la <Link to="/privacy" className="text-gold-700 hover:text-gold-700-700-600">politique de confidentialité</Link>.
        </label>

        <button type="submit" disabled={busy}
          className="w-full px-5 py-3 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-500-400 shadow-gold disabled:opacity-60"
          data-testid="register-submit-button">
          {busy ? "Création…" : "Créer mon compte"}
        </button>
        <div className="text-center text-xs text-muted2">
          Déjà inscrit ? <Link to="/login" className="text-gold-700 hover:text-gold-700-700-600">Se connecter</Link>
        </div>
      </form>
    </div>
  );
}
